/**
 * Auth context, role types and helpers. Kept apart from <AuthProvider> so the
 * context object survives hot reloads of the provider (a new context object
 * makes every page throw "useAuth must be used within AuthProvider").
 */
import { createContext } from "react";
import type { User, LoginRequest, MfaMethod, OrgOption } from "@/types";

/** Clinical roles supported by the Practice UI. Backend may send "provider" (alias for physician). */
export type ClinicalRole =
  | "physician"
  | "provider"
  | "nurse"
  | "medical_assistant"
  | "front_desk"
  | "billing_staff"
  | "org_admin"
  | "staff";

/**
 * All valid clinical roles for runtime checks.
 * "staff" is the primary role for non-provider users whose access comes from
 * RBAC roles (e.g. PRP counselors); the sidebar is built from their
 * effective permissions.
 */
export const CLINICAL_ROLES: ClinicalRole[] = [
  "physician",
  "provider",
  "nurse",
  "medical_assistant",
  "front_desk",
  "billing_staff",
  "org_admin",
  "staff",
];

/** Check if a role string is a valid clinical role. */
export function isClinicalRole(role: string): role is ClinicalRole {
  return CLINICAL_ROLES.includes(role as ClinicalRole);
}

/** Shared result for password login, org selection, and Entra login steps. */
export interface LoginStepResult {
  mfaRequired: boolean;
  mfaToken?: string;
  mfaMethod?: MfaMethod;
  mfaMethods?: MfaMethod[];
  mfaEmailSent?: boolean;
  mfaSmsSent?: boolean;
  mfaSetupRequired?: boolean;
  setupToken?: string;
  orgSelectionRequired?: boolean;
  selectionToken?: string;
  organizations?: OrgOption[];
  authMode?: "local" | "entra";
}

/** Keep practice-eligible clinics only (role must be usable in this portal). */
export function filterPracticeOrgOptions(orgs: OrgOption[] | undefined): OrgOption[] {
  if (!orgs?.length) return [];
  return orgs.filter((o) => isClinicalRole(o.role));
}

export interface AuthContextValue {
  /** Currently authenticated user, or null. */
  user: User | null;
  /** Whether the user is authenticated. */
  isAuthenticated: boolean;
  /** Whether auth state is still loading on mount. */
  isLoading: boolean;
  /** The user's clinical role, or null if not authenticated. */
  clinicalRole: ClinicalRole | null;
  /** Check if the current user has one of the given roles. */
  hasRole: (...roles: ClinicalRole[]) => boolean;
  /** Log in with credentials. May return MFA challenge or multi-org picker. */
  login: (req: LoginRequest) => Promise<LoginStepResult>;
  /** Finish login after the user picks an organization. */
  selectOrg: (
    selectionToken: string,
    organizationId: string,
    authMode?: "local" | "entra",
  ) => Promise<LoginStepResult>;
  /** Complete MFA login: exchange mfa_token + code for tokens and user. */
  completeMFA: (mfaToken: string, code: string) => Promise<void>;
  /** Complete MFA enrollment during onboarding (setup_token flow). */
  completeMFASetup: (setupToken: string, code: string) => Promise<void>;
  /** Redirect to Microsoft Entra sign-in. */
  loginWithMicrosoft: () => Promise<void>;
  /** Finish Entra redirect - exchange id_token with backend. May return org picker. */
  completeEntraLogin: (idToken: string) => Promise<LoginStepResult>;
  /** Log the current user out. */
  logout: () => Promise<void>;
  /** Force-refresh the current user profile from the server. */
  refreshAuth: () => Promise<User | null>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
